/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { isMembershipSetupFeeLine } from '@notifi/shared-types/membership-line';
import type { EstimateDocTotalsOverride } from '@/components/documents/estimates/blocks/EstimateDocumentRenderer';
import type { PdfCustomFieldValue } from '@/components/documents/estimates/pdfTypes';
import type { SignaturePadHandle } from '@/components/molecules/SignaturePad';
import type { PublicPaymentPlanPreview } from '@/components/public/PaymentPlanPreview';
import {
  isBankOnly,
  offersTwoDepositMethods,
  type DepositMethod,
} from '@/components/organisms/payments/DepositCTA';
import type { PublicReviewSummary } from '@/components/public/SingleScopeReviews';
import {
  estimateWithAcceptedMembershipOffer,
  membershipOfferForBillingOption,
  membershipOfferFromSnapshot,
  membershipOfferForSelectedLines,
  membershipOfferTaxTotal,
} from '@/lib/sales/membership-offer';
import { buildMembershipTotalsOverride } from '@/lib/quote/membershipTotalsOverride';
import { resolveSingleScopeCompany } from '@/lib/quote/singleScopeCompany';
import { layoutVisibility } from '@/lib/quote/layoutVisibility';
import {
  requireApprovedEstimateForSignedMembership,
  seedApprovedPublicEstimate,
  publicEstimateQueryPrefix,
} from '@/lib/quote/approvedEstimateCache';
import { refetchPublicEstimate } from '@/lib/quote/postApprovalRefetch';
import {
  fetchPublicPaymentPlanPreview,
  publicPaymentPlanKey,
} from '@/lib/quote/publicPaymentPlanCache';
import {
  prepareEstimatePdfImages,
  mergeEstimatePdfImages,
} from '@/lib/sales/prepare-pdf-line-images';
import { registerSignedPdf, renderSignedPdf } from '@/lib/quote/registerSignedPdf';
import { useEstimateSelections } from '@/lib/quote/useEstimateSelections';
import type { EstimateDocLayout } from '@notifi/shared-types/estimate-doc-template';
import type { CustomerApprovalMethod, Estimate, EstimateStatus } from '@/types/estimate';
import { resolveCustomerTerms } from '@/lib/terms/clientTerms';
import {
  isApprovalBlockedByDeposit,
  isDepositOwed,
  rescaleDepositSurcharge,
  shouldShowDepositCta,
} from '@/lib/quote/depositCta';
import { estimatePdfBaseName } from '@/lib/quote/pdfFileName';
import { offlineApprovalNote } from '@/lib/quote/offlineApprovalNote';
import { type EstimatePresentation } from '@/types/settings';
import { publicPost } from '@/lib/api/publicClient';
import { isApiError } from '@/lib/api/errors';
import type {
  CreateStripeSessionResponse,
  PublicPaymentConfig,
  PublicWisetackCta,
} from '@/types/public';
import { documentDiscountLabel } from '@/lib/sales/document-discount-label';
import { reconciledTaxBreakdown } from '@/lib/tax/tax-utils';
import { dateInTimeZone, dateStringToDate, formatDateTimeInTimeZone } from '@/lib/date-values';
import { formatDateOnly } from '@/lib/dates/date-only';
import { canUseStoredEstimatePdf, getEstimateCustomerNotes } from '@/lib/sales/estimate-rendering';
import {
  estimateCustomerVisibleTitle,
  estimateServiceAddressText,
} from '@/lib/sales/estimate-document-address';
import { estimateForAcceptedScope } from '@/lib/estimate/acceptedScope';
import { selectedDepositAmount } from '@/lib/quote/selectedDeposit';

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const OPS_URL = import.meta.env.VITE_OPERATIONS_URL ?? '';

export function fmt(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function fmtDate(iso: string, timezone?: string | null): string {
  // SPEC-tz-6 — business INSTANTS render in the company tz (from the public
  // payload), not the viewer's locale tz.
  return formatDateTimeInTimeZone(iso, timezone ?? undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Date-only @db.Date fields (e.g. validUntil) are tz-less — parse at local
// midnight so they never shift a day for viewers behind UTC. Do NOT use fmtDate.
// The formatting itself lives in `@/lib/dates/date-only`, the one implementation
// every surface shares; this only adds the public-document placeholder.
export function fmtDateOnly(value?: string | null): string {
  return value ? formatDateOnly(value) : '—';
}

function discountLabel(estimate: Estimate): string {
  return documentDiscountLabel({
    promotionId: estimate.promotionId,
    discountReason: estimate.discountReason,
    discountType: estimate.discountType,
    discountValue: estimate.discountValue,
  });
}

export interface CompanyInfo {
  name: string;
  logoUrl?: string | null;
  /** NOT-236 — inline data URI of the logo, embedded into the customer PDF so
   *  react-pdf doesn't fetch the presigned S3 URL cross-origin (blocked by S3
   *  CORS, which blanks the logo). The on-screen header still uses `logoUrl`. */
  logoDataUri?: string | null;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  } | null;
  email?: string | null;
  phone?: string | null;
  /** NOT-7 — tax-registration number printed under the company header. */
  taxRegistrationNumber?: string | null;
  /** Marketing line under the company name on the single-scope rail. */
  tagline?: string | null;
  /** Contractor/trade license number(s) shown on the rail. */
  contractorLicense?: string | null;
  regulatoryNotice?: string | null;
}

export interface PublicCustomFieldValue {
  fieldDefinitionId: string;
  fieldKey: string;
  label: string;
  value: string;
}

/**
 * NOT-305 — credit-card surcharge preview for the deposit. Computed server-
 * side so the disclosed total matches what a CARD deposit is charged.
 * `enabled` is false when the checkout would not surcharge a card payment
 * (tenant doesn't surcharge, Stripe isn't the active processor, card isn't
 * available, or there's no deposit). `achNoFeeOffered` is true when the
 * customer is also offered an unsurcharged bank-transfer option — i.e. the
 * two-option deposit CTA.
 */
export interface DepositSurcharge {
  enabled: boolean;
  surchargePercent: number;
  achCheckDiscountEnabled?: boolean;
  achCheckDiscountPercent?: number;
  discountCents?: number;
  surchargeAmount: number;
  totalWithSurcharge: number;
  achNoFeeOffered: boolean;
  /**
   * Rails the customer is actually offered, server-resolved from the same list
   * the checkout-session path builds. Rail availability is independent of
   * surcharging — a tenant that accepts bank transfers but charges no card fee
   * must still be offered the bank rail. Optional for back-compat with cached
   * payloads that predate the fields.
   */
  cardOffered?: boolean;
  achOffered?: boolean;
}

export interface PublicEstimateResponse {
  estimate: Estimate;
  staleItems: unknown[];
  company: CompanyInfo | null;
  customFields: PublicCustomFieldValue[];
  estimateSettings?: { presentation?: EstimatePresentation };
  activePdf?: { url: string; reason?: string | null } | null;
  depositCollectedSummary?: Estimate['depositCollectedSummary'];
  depositSurcharge?: DepositSurcharge;
  /**
   * Which pay UI the deposit CTA must render — the tenant's processor plus, on
   * Payabli, the embedded-component config. Absent/null on payloads that
   * predate the block (and whenever payment config can't be resolved), which
   * the CTA treats as the historical Stripe-Checkout behaviour.
   */
  payment?: PublicPaymentConfig | null;
  /**
   * The newest inline (Payabli) deposit attempt's settlement state for an
   * uncollected deposit — PENDING keeps the page polling for the webhook
   * settlement, FAILED flips the "payment submitted" banner to a retryable
   * failure. Null when the deposit is collected or nothing stands.
   */
  depositAttempt?: { status: string; rail?: 'BANK' | 'CARD' } | null;
  /** P4 — customer-facing service-fee (card surcharge) disclosure. */
  serviceFee?: {
    show: boolean;
    percent: number;
    chargedAmount?: number | null;
    chargedTotal?: number | null;
  };
  wisetack?: PublicWisetackCta | null;
  /** Company/business IANA timezone for rendering customer-facing dates (SPEC-tz-6). */
  timezone?: string | null;
  /** Customer-facing attribution — name only (id stays stripped server-side). */
  presentedBy?: { name: string } | null;
  /** Aggregate social proof from synced external reviews; null when none. */
  reviews?: PublicReviewSummary | null;
  /**
   * The resolved block-document layout frozen onto this estimate, or null. Null
   * for every estimate with no custom template (the norm) — those render exactly
   * as before. Non-null only when a template is applied, in which case the page
   * renders the document through the block renderer.
   */
  resolvedDocLayout?: EstimateDocLayout | null;
}

/**
 * Body of POST /estimates/public/:id/customer-approve.
 *
 * This is the repriced estimate: approval re-runs pricing when the customer
 * changed an optional selection, so `depositAmount` here can differ from the
 * one this page loaded with, and `depositSurcharge` is derived from the new
 * amount. Both fields are optional because the parse falls back to `{}` — an
 * approval that already committed must not be reported as failed just because
 * its response body was unreadable.
 */
export interface CustomerApproveResponse {
  estimate?: Partial<Estimate>;
  depositCollectedSummary?: Estimate['depositCollectedSummary'];
  depositSurcharge?: DepositSurcharge;
}

export async function fetchEstimate(id: string): Promise<PublicEstimateResponse> {
  const res = await fetch(`${OPS_URL}/estimates/public/${id}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error('Estimate not found');
  const data = await res.json();
  // Normalize: if old API returns bare estimate, wrap it
  if (data.estimate) return data as PublicEstimateResponse;
  return {
    estimate: data as Estimate,
    staleItems: [],
    company: null,
    customFields: [],
  };
}

async function customerApprove(
  id: string,
  tenantId: string,
  body: {
    method: CustomerApprovalMethod;
    acceptMembershipOffer?: boolean;
    membershipBillingOptionId?: string;
    customerSignatureName?: string;
    customerSignatureText?: string;
    customerSignatureImage?: string;
    // NOT-590 — `lineItemId` is the catalog id and can repeat across lines
    // (the checkbox state above is keyed per-line via `optionalKey` for the
    // same reason). `lineKey` is the per-line identity; sending it lets the
    // server flip exactly the line the customer ticked instead of every
    // duplicate of the same catalog item. Omitted only for legacy lines saved
    // before `lineKey` existed.
    lineItems: { lineItemId: string; lineKey?: string; isSelected: boolean }[];
    consentGranted?: boolean;
    consentPaymentMethodId?: string;
    consentMaxAmountPerCharge?: number;
    consentMaxTotal?: number;
    consentUserAgent?: string;
  },
): Promise<CustomerApproveResponse> {
  const res = await fetch(`${OPS_URL}/estimates/public/${id}/customer-approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-id': tenantId,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const message = await res
      .json()
      .then((d: { message?: string }) => d?.message)
      .catch(() => undefined);
    throw new Error(message || 'Failed to approve estimate');
  }
  // The body is the repriced estimate — read it. A malformed/empty body must
  // not fail an approval that already committed server-side, so fall back to
  // an empty envelope and let the caller keep its pre-approval values.
  return (await res.json().catch(() => ({}))) as CustomerApproveResponse;
}

async function customerDecline(id: string, tenantId: string, reason: string): Promise<void> {
  const res = await fetch(`${OPS_URL}/estimates/public/${id}/reject`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-id': tenantId,
    },
    body: JSON.stringify({ reason: reason.trim() || undefined }),
  });
  if (!res.ok) throw new Error('Failed to decline estimate');
}

// NOT-305 — when the tenant surcharges and offers both card + ACH, the
// customer picks a method on the deposit CTA. 'card' creates a surcharged
// card-only session; 'ach' creates an unsurcharged bank-transfer session.
// Omitted = legacy single-session behaviour (server resolves the method).
// `DepositMethod` is owned by the DepositCTA component and imported above.

/**
 * The optional lines the customer ticked, sent with a REQUIRED deposit paid
 * before approval (`depositBeforeApproval`) so the server prices the deposit
 * on that scope (and then holds the approval to it). Omitted when the deposit
 * is paid after approval, when the scope is already recorded.
 */
export interface DepositSelection {
  lineItems: { lineItemId: string; lineKey?: string; isSelected: boolean }[];
}

async function createDepositStripeSession(
  id: string,
  method?: DepositMethod,
  selection?: DepositSelection,
): Promise<CreateStripeSessionResponse> {
  return publicPost(`/estimates/public/${id}/stripe-deposit-session`, {
    ...(method ? { method } : {}),
    ...(selection ? { lineItems: selection.lineItems } : {}),
  });
}

/**
 * Payer-safe result of a Payabli deposit charge. `depositCollected` is the
 * server's re-read of the ESTIMATE, not an inference from `status` — an ACH
 * charge can settle asynchronously (PENDING), and claiming a deposit that has
 * not landed would flip the page to "Paid" over money still in flight.
 */
export interface PayabliDepositChargeResult {
  status: string;
  amount: number;
  surchargeAmount: number;
  depositCollected: boolean;
}

/**
 * Charge the deposit through Payabli. Unlike the Stripe path (which only opens
 * a hosted-checkout session), this MOVES MONEY on call. The body carries only
 * the embedded component's short-lived token — the server derives the tenant,
 * the payer and the AMOUNT from the estimate itself.
 */
async function chargeDepositPayabli(
  id: string,
  temporaryToken: string,
  surchargeDisclosed: boolean,
  selection?: DepositSelection,
): Promise<PayabliDepositChargeResult> {
  return publicPost(`/estimates/public/${id}/payabli-deposit`, {
    temporaryToken,
    // Attest the fee note was rendered — the backend only ever adds the card
    // service fee when the page disclosed it, and only for a card rail.
    ...(surchargeDisclosed ? { surchargeDisclosed: true } : {}),
    ...(selection ? { lineItems: selection.lineItems } : {}),
  });
}

const ACTIONABLE_STATUSES: EstimateStatus[] = ['SENT', 'VIEWED', 'FOLLOW_UP'];

export interface AttachmentImage {
  id: string;
  downloadUrl: string;
  fileName: string;
}

/** The portal reuses the document, but keeps authenticated actions and its own cache. */
export interface QuoteActionTransport {
  queryKey: readonly unknown[];
  approve: typeof customerApprove;
  decline: typeof customerDecline;
  createDepositSession: typeof createDepositStripeSession;
  chargeDeposit: typeof chargeDepositPayabli;
}

export interface EstimateViewProps {
  /** Customer-facing identity when the canonical checkout is embedded in a proposal. */
  documentLabel?: 'Estimate' | 'Proposal';
  documentNumber?: string;
  transport?: QuoteActionTransport;
  /** Explicit portal payer; undefined retains the public estimate-owner behavior. */
  payerContactId?: string | null;
  /** The portal shell already owns the scroll container. */
  embedded?: boolean;
  estimate: Estimate;
  company: CompanyInfo | null;
  activePdf: { url: string; reason?: string | null } | null;
  customFields: PdfCustomFieldValue[];
  presentation?: EstimatePresentation;
  presentedByName?: string | null;
  reviews?: PublicReviewSummary | null;
  id: string;
  depositSearchState?: 'success' | 'cancel';
  depositSurcharge?: DepositSurcharge;
  payment?: PublicPaymentConfig | null;
  /** Newest inline deposit attempt's settlement state (see PublicEstimateResponse). */
  depositAttempt?: { status: string; rail?: 'BANK' | 'CARD' } | null;
  serviceFee?: {
    show: boolean;
    percent: number;
    chargedAmount?: number | null;
    chargedTotal?: number | null;
  };
  wisetack?: PublicWisetackCta | null;
  timezone?: string | null;
  /** Resolved block-document layout frozen onto the estimate, or null. */
  resolvedDocLayout?: EstimateDocLayout | null;
  /**
   * Take a deposit marked "Required to approve" BEFORE approval and lock
   * approval until it is in. Proposal checkouts always set this; directly
   * opened estimates originating from a proposal enforce it too.
   */
  depositBeforeApproval?: boolean;
  /**
   * Open the deposit payment once, as soon as it is payable: the customer
   * has just approved a proposal option carrying a deposit (Approve and pay).
   */
  openDepositOnLoad?: boolean;
}

// ─── Action-layer state hook ────────────────────────────────────────────────
//
// Owns the money/action layer of the public estimate page: the ~16 pieces of
// approve/decline/deposit/signature state, the estimate-scoped queries, the
// selection engine, the derived money values, and the approve/decline/download
// handlers. Exposed to the view and its slot components through
// QuoteActionContext so the money-critical wiring lives in exactly one place.
function useQuoteActionState({
  estimate,
  company,
  activePdf,
  customFields,
  presentation = 'ITEMIZED',
  presentedByName = null,
  reviews = null,
  id,
  depositSearchState,
  depositSurcharge,
  payment = null,
  depositAttempt = null,
  serviceFee,
  wisetack,
  timezone,
  resolvedDocLayout = null,
  transport,
  payerContactId,
  embedded = false,
  documentLabel = 'Estimate',
  documentNumber,
  depositBeforeApproval = false,
  openDepositOnLoad = false,
}: EstimateViewProps) {
  // Field visibility follows the resolved DESIGN: the same blocks the renderer
  // draws decide what the single-scope surfaces and the file-name / expiry /
  // offline-note helpers may show.
  const v = layoutVisibility(resolvedDocLayout);
  // Used to refresh the public payload after approval so the deposit CTA can
  // never render pre-reprice amounts (see refetchPublicEstimate).
  const queryClient = useQueryClient();
  const estimateQueryKey = useMemo(
    () => transport?.queryKey ?? publicEstimateQueryPrefix(id),
    [transport?.queryKey, id],
  );

  // Saved-PDF filename: omit the estimate number when the merchant has hidden it
  // (the design hides the estimate number), so it never leaks into the customer's downloads.
  const pdfBaseName = estimatePdfBaseName({
    estimateNumber: estimate.estimateNumber,
    showEstimateNumber: v.estimateNumber,
  });
  const serviceAddress = estimateServiceAddressText(estimate);
  const customerVisibleTitle = estimateCustomerVisibleTitle(estimate);
  const estimateDiscountLabel = discountLabel(estimate);
  const customerNotes = getEstimateCustomerNotes(estimate);
  const { data: attachmentImages = [] } = useQuery<AttachmentImage[]>({
    queryKey: ['public', 'estimate', id, 'images'],
    queryFn: async () => {
      const res = await fetch(`${OPS_URL}/estimates/public/${id}/attachments/images`);
      if (!res.ok) return [];
      return res.json();
    },
    retry: false,
  });
  const { data: paymentPlan = null } = useQuery<PublicPaymentPlanPreview | null>({
    queryKey: publicPaymentPlanKey(id),
    queryFn: () =>
      fetchPublicPaymentPlanPreview<PublicPaymentPlanPreview>({
        operationsUrl: OPS_URL,
        id,
        tenantId: estimate.tenantId,
      }),
    retry: false,
  });
  // Pre-fill the signer with the customer on record (the person the estimate
  // was sent to) — a sensible default they can still edit before signing.
  // Filled in below once the selection engine has run; read by the deposit
  // calls at click time (a ref, so the call sites need not be declared after
  // the values they carry).
  const depositSelectionRef = useRef<DepositSelection | undefined>(undefined);
  const [sigName, setSigName] = useState(
    estimate.customer?.primaryContactName?.trim() || estimate.customer?.name?.trim() || '',
  );
  const [sigNote, setSigNote] = useState('');
  // Whether the signature pad has any ink — drives the "Draw your signature
  // here" hint in the single-scope approve modal.
  const [sigHasInk, setSigHasInk] = useState(false);
  // Whether the estimate's terms are expanded inline inside the approve modal,
  // so the customer can read what they're accepting without a modal-on-modal.
  const [showModalTerms, setShowModalTerms] = useState(false);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  // The approval flow (signature / consent / terms) lives in a shared modal for
  // every public estimate presentation.
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  // Radix does not call onOpenChange when a controlled dialog is closed by
  // setting its open prop. Reset transient presentation state for every close
  // path, including Cancel, X, and a successful approval.
  useEffect(() => {
    if (!approveModalOpen) {
      setSigHasInk(false);
      setShowModalTerms(false);
    }
  }, [approveModalOpen]);
  // Single-scope only: Decline is a link in the document footer that opens this
  // modal (quick reasons + free-text "Other"), rather than a mid-page form.
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  // The picked decline reason (a preset label or the 'Other' sentinel); null
  // until the customer chooses one. 'Other' reveals the free-text field.
  const [declineChoice, setDeclineChoice] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [consentGranted, setConsentGranted] = useState(false);
  const [consentRequired, setConsentRequired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [redirectingDeposit, setRedirectingDeposit] = useState(false);
  const sigPadRef = useRef<SignaturePadHandle>(null);
  const [actionDone, setActionDone] = useState<'approved' | 'declined' | null>(null);
  const [signedPdfBlob, setSignedPdfBlob] = useState<Blob | null>(null);
  const [signedPdfBlobUrl, setSignedPdfBlobUrl] = useState<string | null>(null);

  // Track which method-specific session is in flight so the right button shows
  // its spinner when the two-option (card / bank transfer) CTA is rendered.
  const [pendingDepositMethod, setPendingDepositMethod] = useState<DepositMethod | null>(null);
  const depositMutation = useMutation({
    mutationFn: (method?: DepositMethod) =>
      (transport?.createDepositSession ?? createDepositStripeSession)(
        id,
        method,
        depositSelectionRef.current,
      ),
    onSuccess: (data) => {
      const url =
        data.url ?? (data.sessionId ? `https://checkout.stripe.com/pay/${data.sessionId}` : null);
      if (!url) {
        toast.error('We couldn’t open the secure checkout. Please try again.');
        setPendingDepositMethod(null);
        return;
      }
      setRedirectingDeposit(true);
      window.location.href = url;
    },
    onError: (error) => {
      // The concurrent-deposit guards reject with a retryable 4xx whose copy
      // ("…already being started… try again in a moment") is the whole reason
      // retrying is safe — the generic line makes that recoverable case look
      // like a hard payment failure. 5xx/network/timeout messages stay hidden:
      // those are transport noise, not copy written for the customer.
      const serverMessage =
        isApiError(error) && error.status >= 400 && error.status < 500 ? error.message.trim() : '';
      toast.error(serverMessage || 'Failed to initiate deposit payment. Please try again.');
      setPendingDepositMethod(null);
    },
  });
  const startDeposit = (method?: DepositMethod) => {
    setPendingDepositMethod(method ?? null);
    depositMutation.mutate(method);
  };

  // Approve and pay: once the approval lands with a deposit due, the payment
  // opens by itself. A single Stripe method goes straight to Checkout. Two
  // Stripe methods (bank + card) are the customer's choice, so the deposit
  // slot only scrolls to them and focuses the first; Payabli opens its form
  // in place. `depositAutoOpen` is what the slot reads.
  const [depositAutoOpen, setDepositAutoOpen] = useState(false);
  const openDepositPayment = (surcharge: DepositSurcharge | undefined) => {
    const onStripe = !payment || payment.processor === 'stripe';
    if (onStripe && !offersTwoDepositMethods(surcharge)) {
      startDeposit(isBankOnly(surcharge) ? 'ach' : undefined);
      return;
    }
    setDepositAutoOpen(true);
  };
  const payFirst =
    (depositBeforeApproval || estimate.source === 'PROPOSAL') && Boolean(estimate.depositRequired);

  // An inline Payabli ACH deposit came back PENDING: poll the public estimate
  // until the webhook settles it (depositCollected flips → CTA unmounts) or
  // the attempt terminally fails (banner flips to its retry state). Without
  // this the "we will confirm your deposit shortly" banner never resolves —
  // the page's own deposit poll only runs on the Stripe ?deposit=success
  // redirect.
  const [achDepositPending, setAchDepositPending] = useState(false);
  // Server-derived too: `depositAttempt` reports PENDING only for an attempt
  // that reached the provider, so a RELOAD during an unsettled ACH deposit
  // resumes the submitted state + poll instead of re-offering the pay CTA
  // over money in flight.
  const depositAttemptStatus = depositAttempt?.status?.toUpperCase();
  const serverDepositSettling = depositAttemptStatus === 'PENDING' && !estimate.depositCollected;
  // Only a failure that follows an observed settlement flips the CTA to its
  // failure state; a cold load after an old failed attempt is just the CTA.
  const sawDepositSettlingRef = useRef(false);
  if (achDepositPending || serverDepositSettling) {
    sawDepositSettlingRef.current = true;
  }
  // Once the server records the payment clearing, the slot shows that instead
  // and the poll stops: a bank transfer settles in days, not seconds.
  const depositAchSettling =
    !estimate.depositCollected &&
    !estimate.depositClearing &&
    depositAttemptStatus !== 'FAILED' &&
    (serverDepositSettling || achDepositPending);
  const depositAchFailed =
    sawDepositSettlingRef.current && !depositAchSettling && depositAttemptStatus === 'FAILED';
  useEffect(() => {
    if (!depositAchSettling || depositAchFailed) {
      return;
    }
    const timer = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: estimateQueryKey });
    }, 4000);
    return () => clearInterval(timer);
  }, [depositAchSettling, depositAchFailed, estimateQueryKey, queryClient]);

  // Payabli deposit: the embedded component hands us a one-time token and the
  // server charges the estimate's own deposit immediately. Awaited (not
  // fire-and-forget) so PayabliDepositPayment can hold its "Processing…" state
  // for the whole round-trip and surface a failure back into the card form.
  const payDepositWithPayabli = async (
    temporaryToken: string,
    // The CTA reports whether the payer was on the card rail (and so saw the
    // fee note). A bank payer was told "no fee" and must not attest one.
    surchargeDisclosed: boolean,
  ): Promise<'SUCCEEDED' | 'PENDING'> => {
    const result = await (transport?.chargeDeposit ?? chargeDepositPayabli)(
      id,
      temporaryToken,
      surchargeDisclosed,
      depositSelectionRef.current,
    );
    // Trust the settled status, not the 200: a soft decline comes back as a
    // persisted FAILED row with HTTP 200 and must surface as an error, not a
    // thank-you toast over an uncollected deposit.
    const status = result.status?.toUpperCase();
    if (status !== 'SUCCEEDED' && status !== 'PENDING') {
      throw new Error('Deposit payment could not be completed. Please try another payment method.');
    }
    if (result.depositCollected) {
      toast.success('Deposit paid. Thank you!');
    } else {
      // An ACH capture can settle asynchronously — say so rather than claiming
      // a deposit the estimate does not yet hold.
      toast.success('Payment submitted. We will confirm your deposit shortly.');
    }
    // Refetch flips the panel to its paid state (or leaves the CTA up for a
    // still-settling payment). The CTA component branches on the outcome so a
    // PENDING settlement renders a terminal "submitted" state instead of
    // spinning forever under a still-payable estimate.
    await queryClient.invalidateQueries({ queryKey: estimateQueryKey });
    const outcome = status === 'SUCCEEDED' && result.depositCollected ? 'SUCCEEDED' : 'PENDING';
    if (outcome === 'PENDING') {
      // Start polling for the webhook settlement (see the effect above).
      setAchDepositPending(true);
    }
    return outcome;
  };

  // The signed PDF is the document of record, so it stays on screen even when
  // financing is offered. `!wisetack` used to be ANDed in here to force a live
  // re-render, which split this page into two different-looking layouts for the
  // same estimate. It is not needed: the signed PDF is rendered at approval
  // time from <EstimateDocumentPdf wisetack={...}>, so a document approved while
  // financing was active already carries its placement, and the financing CTA
  // renders beside the document from the approved aside either way.
  const shouldUseStoredEstimatePdf = canUseStoredEstimatePdf(estimate, activePdf);

  // Conversion to a job does not invalidate the customer's signed document.
  const { data: downloadUrlData } = useQuery<{ url: string } | null>({
    queryKey: ['public', 'estimate', id, 'pdf-download-url'],
    queryFn: async () => {
      const res = await fetch(`${OPS_URL}/estimates/public/${id}/pdf/download-url`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled:
      (estimate.status === 'APPROVED' || estimate.status === 'CONVERTED') &&
      shouldUseStoredEstimatePdf &&
      !signedPdfBlob,
    retry: false,
    staleTime: 240_000, // 4 min — presigned URL expires in 5 min
  });

  // If signed this session use a blob URL, otherwise use the fetched presigned URL
  const pdfDisplayUrl = signedPdfBlobUrl ?? downloadUrlData?.url ?? null;

  // The customer-facing selection engine (optional add-ons + live reprice),
  // shared verbatim with the portal estimate detail via useEstimateSelections.
  // Once approval commits, the live page becomes the accepted document: a
  // declined option stays in storage for audit but disappears from the view.
  const presentedEstimate = useMemo(
    () => estimateForAcceptedScope(estimate, actionDone === 'approved'),
    [actionDone, estimate],
  );
  const {
    optionalSelections,
    toggleOptional,
    requiredRows,
    displayOptionalOffers,
    optionalSelectionsChanged,
    selectedLines,
    totals,
    requiredSubtotal,
    selectedOptionalRows,
    approvalLineItems,
    selectedGrandTotal,
    selectedMembershipDiscountTotal,
    selectedDiscountTotal,
    selectedDeposit: selectionDeposit,
    selectedEstimate,
  } = useEstimateSelections(presentedEstimate);

  const canAct = ACTIONABLE_STATUSES.includes(estimate.status) && actionDone === null;

  // An estimate's status doesn't auto-flip to EXPIRED the moment `validUntil`
  // passes (the daily scanner sweeps at midnight in the tenant's timezone), so
  // an estimate can sit in an ACTIONABLE status while already past its
  // valid-until date. The BE approval guard only checks status, so a
  // date-expired quote would otherwise approve with stale pricing.
  //
  // "Today" is the BUSINESS's today, not the viewer's (SPEC-tz-6). validUntil
  // is a tz-less @db.Date naming a day on the company's calendar, and the
  // scanner judges it against that same calendar — so a customer reading the
  // quote from a timezone ahead of the business would otherwise be told it
  // expired while the business still considers it live, and be blocked from
  // approving it. The payload always carries a resolved zone — the endpoint
  // applies DEFAULT_TENANT_TZ rather than sending `null` — so this gate and
  // the backend guard cannot pick different defaults.
  const validUntilDate = dateStringToDate(estimate.validUntil);
  const isExpiredByDate = (() => {
    if (!validUntilDate) return false;
    const today = dateInTimeZone(new Date().toISOString(), timezone);
    if (!today) return false;
    return today.getTime() > validUntilDate.getTime();
  })();
  const expiredWhileActionable = canAct && isExpiredByDate;

  // NOT-625 — only expose an actionable, fully-priced offer. A frozen offer is
  // not a generic advertisement: after expiry/approval it can no longer be
  // accepted, and a plan without a frozen price cannot produce a truthful CTA.
  const baseMembershipOffer = useMemo(() => {
    if (!canAct || isExpiredByDate) return null;
    const frozen = membershipOfferFromSnapshot(estimate.membershipPlanSnapshot);
    return frozen?.planPrice == null
      ? null
      : membershipOfferForSelectedLines(frozen, selectedLines, estimate.lineItems);
  }, [canAct, estimate.lineItems, estimate.membershipPlanSnapshot, isExpiredByDate, selectedLines]);
  const [selectedMembershipBillingOptionId, setSelectedMembershipBillingOptionId] = useState<
    string | null
  >(null);
  const membershipOffer = useMemo(
    () => membershipOfferForBillingOption(baseMembershipOffer, selectedMembershipBillingOptionId),
    [baseMembershipOffer, selectedMembershipBillingOptionId],
  );
  const [acceptMembershipOffer, setAcceptMembershipOffer] = useState(false);
  const acceptedMembershipGrandTotal = useMemo(() => {
    if (!membershipOffer || membershipOffer.planPrice == null) return null;
    return (
      Math.round(
        (membershipOffer.memberGrandTotal +
          membershipOffer.planPrice +
          (membershipOffer.setupFee ?? 0) +
          Number.EPSILON) *
          100,
      ) / 100
    );
  }, [membershipOffer]);
  const acceptingMembershipOffer = acceptMembershipOffer && acceptedMembershipGrandTotal !== null;
  const membershipAdjustedEstimate = useMemo(
    () =>
      acceptingMembershipOffer && membershipOffer
        ? estimateWithAcceptedMembershipOffer(selectedEstimate, membershipOffer)
        : selectedEstimate,
    [acceptingMembershipOffer, membershipOffer, selectedEstimate],
  );
  useEffect(() => {
    if (!baseMembershipOffer) {
      setAcceptMembershipOffer(false);
      setSelectedMembershipBillingOptionId(null);
      return;
    }
    setSelectedMembershipBillingOptionId((current) =>
      baseMembershipOffer.billingOptions?.some((option) => option.id === current)
        ? current
        : (baseMembershipOffer.billingOptionId ??
          baseMembershipOffer.billingOptions?.[0]?.id ??
          null),
    );
  }, [baseMembershipOffer]);
  const approvalGrandTotal = acceptingMembershipOffer
    ? acceptedMembershipGrandTotal
    : selectedGrandTotal;
  const acceptedMembershipTaxTotal = useMemo(
    () => membershipOfferTaxTotal(membershipOffer, selectedLines, estimate.lineItems),
    [estimate.lineItems, membershipOffer, selectedLines],
  );
  const approvalTaxTotal =
    acceptingMembershipOffer && acceptedMembershipTaxTotal !== null
      ? acceptedMembershipTaxTotal
      : totals.taxTotal;
  // What the customer saves through membership on this quote. An existing
  // member accepting an offer for ANOTHER plan keeps the savings of the plan
  // they hold (the lines are already priced with it) and adds what joining the
  // offered plan saves on top — the offer's `savings` is that increment, not a
  // replacement. A non-member's current savings are 0, so this is the offer's.
  const displayMembershipSavings = acceptingMembershipOffer
    ? Math.round(
        (selectedMembershipDiscountTotal + (membershipOffer?.savings ?? 0) + Number.EPSILON) * 100,
      ) / 100
    : selectedMembershipDiscountTotal;
  // Names the plans those savings come from, so the row never credits the
  // wrong plan: "(Gold)" for a member, "(Gold + Platinum)" while that member
  // accepts Platinum, "(Platinum)" for a non-member accepting it.
  const membershipSavingsPlanNames = (() => {
    const names: string[] = [];
    const heldPlan = estimate.membershipPlanSnapshot?.planName;
    if (selectedMembershipDiscountTotal > 0 && heldPlan) names.push(heldPlan);
    if (acceptingMembershipOffer && membershipOffer?.planName) {
      names.push(membershipOffer.planName);
    }
    return names.join(' + ');
  })();
  const membershipPlanPrice = acceptingMembershipOffer ? (membershipOffer?.planPrice ?? 0) : 0;
  const requiredSetupFee = selectedLines
    .filter(
      (line) =>
        isMembershipSetupFeeLine(line) &&
        !line.isOptional &&
        line.displayConfig?.hidePrice !== true,
    )
    .reduce((sum, line) => sum + (line.lineSubtotal ?? line.basePrice ?? 0), 0);
  const membershipSetupFee =
    requiredSetupFee + (acceptingMembershipOffer ? (membershipOffer?.setupFee ?? 0) : 0);
  // The frozen member total excludes the membership subscription itself, so it
  // is also the authoritative work base for a percentage/flat deposit preview.
  // Approval reprices this server-side; this keeps the pre-approval disclosure
  // aligned with the same member-adjusted total the customer is accepting.
  const selectionDrivenDeposit = acceptingMembershipOffer
    ? selectedDepositAmount({
        depositAmount: estimate.depositAmount,
        depositType: estimate.depositType,
        depositValue: estimate.depositValue,
        depositCollected: estimate.depositCollected,
        selectedGrandTotal: membershipOffer?.memberGrandTotal ?? selectedGrandTotal,
        selectedLines: [],
      })
    : selectionDeposit;
  // A deposit taken BEFORE approval is priced by the server on the optional
  // lines ticked, at list prices — the membership offer is an approval-time
  // decision and does not size it. Show that figure while it is still due, so
  // the total, the CTA and the charge agree. Everywhere else (every deposit
  // paid after approval, which reprices it on the accepted scope) the
  // selection-driven (member-aware) preview stands.
  const selectedDeposit =
    canAct && payFirst && !estimate.depositCollected ? selectionDeposit : selectionDrivenDeposit;

  // When membership is accepted the PDF renders the member-adjusted estimate; feed
  // the design PDF the SAME authoritative totals the hosted document uses so its
  // breakdown reconciles. Built by the SHARED helper the portal also uses, so the
  // two signed documents can never drift. Undefined for non-membership PDFs.
  const membershipTotalsOverride: EstimateDocTotalsOverride | undefined = acceptingMembershipOffer
    ? buildMembershipTotalsOverride({
        membershipOffer,
        listSubtotal: totals.subtotal,
        discountTotal: selectedDiscountTotal,
        discountLabel: estimateDiscountLabel,
        deposit: selectedDeposit,
        selectedLines,
        allLines: estimate.lineItems,
        fallbackTaxTotal: totals.taxTotal,
        heldMembershipSavings: selectedMembershipDiscountTotal,
        membershipSavingsLabel: membershipSavingsPlanNames || undefined,
      })
    : undefined;

  // Collect every named jurisdiction from the selected line items. Historical
  // estimates with incomplete snapshots fall back to a rolled-up Tax row.
  const taxBreakdown = useMemo(() => {
    if (!canAct && actionDone !== null) return [];
    return reconciledTaxBreakdown(selectedLines, totals.taxTotal);
  }, [selectedLines, totals.taxTotal, canAct, actionDone]);

  const approvalMethod: CustomerApprovalMethod =
    estimate.customerApprovalMethod ?? (estimate.signatureRequired ? 'SIGNATURE' : 'ONLINE');
  // The single-scope design identity the customer sees (branding-first over the
  // tenant company). Slots and the offline-approval note read this so every
  // identity/contact surface names the same business as the document rail/footer.
  const documentCompany = resolveSingleScopeCompany(company, resolvedDocLayout);
  // EMAIL and VERBAL route approval off this page entirely (no Approve, no
  // Decline), so they owe the customer a channel they can actually use — the
  // DESIGN's contact when it is a branch-branded single-scope quote, not the tenant.
  const offlineNote =
    approvalMethod === 'EMAIL' || approvalMethod === 'VERBAL'
      ? offlineApprovalNote({
          method: approvalMethod,
          companyEmail: documentCompany?.email,
          companyPhone: documentCompany?.phone,
          estimateNumber: estimate.estimateNumber,
          showEstimateNumber: v.estimateNumber,
        })
      : null;
  // NOT-277: strip unfilled fill-in-the-blank clauses (e.g. a deposit "[__]%"
  // placeholder) before showing terms to the customer.
  const terms = resolveCustomerTerms(estimate.termsAndConditions);
  const hasTerms = terms.hasTerms;

  const handleApprove = async () => {
    if (approvalBlockedByDeposit) {
      toast.error('Pay the deposit before approving.');
      return;
    }
    if (approvalMethod === 'SIGNATURE') {
      if (!sigName.trim()) {
        toast.error('Please enter your signature name');
        return;
      }
      if (sigPadRef.current?.isEmpty()) {
        toast.error('Please draw your signature');
        return;
      }
    }
    if (!termsAccepted) {
      toast.error('Please accept the terms to continue');
      return;
    }
    setSubmitting(true);
    try {
      const signatureImage = sigPadRef.current?.getDataURL() ?? undefined;

      const signedPdfContent = (
        estimateForPdf: Estimate,
        paymentPlanForPdf: PublicPaymentPlanPreview | null,
      ) => ({
        estimate: estimateForPdf,
        company,
        signatureDataUrl: signatureImage,
        attachmentImages,
        customFields,
        visibility: v,
        wisetack,
        paymentPlan: paymentPlanForPdf,
        serviceFee,
        timeZone: timezone,
        resolvedDocLayout,
        totalsOverride: membershipTotalsOverride,
        // The design PDF renders line items from the list-priced selection so
        // they reconcile with the list subtotal in the override.
        designEstimate: selectedEstimate,
        presentedByName,
      });

      // Render the signed PDF from a given estimate, upload it and register it
      // as the document of record. Every network step is verified.
      //
      // paymentPlanForPdf is passed explicitly rather than closing over the
      // component's `paymentPlan` — the post-approval call below needs the
      // FRESHLY refetched schedule (grandTotal, per-stage amounts, consent
      // caps), and this handler runs synchronously with respect to React: a
      // cache refetch does not make the new value visible on `paymentPlan`
      // until the next render, which happens after this call already read it.
      const renderAndRegisterSignedPdf = (
        estimateForPdf: Estimate,
        reason: string,
        paymentPlanForPdf: PublicPaymentPlanPreview | null,
      ) =>
        // Shared with the client portal (lib/quote/registerSignedPdf) so the
        // signed document of record is byte-identical across both surfaces.
        registerSignedPdf({
          opsBaseUrl: OPS_URL,
          id,
          reason,
          content: signedPdfContent(estimateForPdf, paymentPlanForPdf),
          onBlob: (blob) => {
            setSignedPdfBlob(blob);
            setSignedPdfBlobUrl(URL.createObjectURL(blob));
          },
        });

      // An optional membership can still be rejected by the server after its
      // final fingerprint/eligibility checks. Render locally before a signature
      // approval so renderer failures remain fail-closed, but do not upload or
      // activate that projected document until approval returns authoritative
      // accepted terms. Ordinary approvals retain their pre-commit legal record.
      const signedMembershipApproval = approvalMethod === 'SIGNATURE' && acceptingMembershipOffer;
      if (acceptingMembershipOffer) {
        if (signedMembershipApproval) {
          const paymentPlanForAcceptance =
            await fetchPublicPaymentPlanPreview<PublicPaymentPlanPreview>({
              operationsUrl: OPS_URL,
              id,
              tenantId: estimate.tenantId,
              acceptMembershipOffer: true,
              membershipBillingOptionId: membershipOffer?.billingOptionId,
            });
          await renderSignedPdf(
            signedPdfContent(membershipAdjustedEstimate, paymentPlanForAcceptance),
          );
        }
      } else {
        try {
          await renderAndRegisterSignedPdf(
            membershipAdjustedEstimate,
            'signature_collected',
            paymentPlan,
          );
        } catch (pdfErr) {
          if (approvalMethod === 'SIGNATURE') {
            throw pdfErr instanceof Error
              ? pdfErr
              : new Error('Failed to save signed PDF. Please try again.');
          }
          console.warn('Estimate PDF storage failed (non-fatal for online approval):', pdfErr);
        }
      }

      const approved = await (transport?.approve ?? customerApprove)(id, estimate.tenantId, {
        method: approvalMethod,
        // Omit the field for ordinary approvals so rolling deployments remain
        // compatible with older strict DTOs that reject unknown properties.
        ...(acceptingMembershipOffer && { acceptMembershipOffer: true }),
        ...(acceptingMembershipOffer && membershipOffer?.billingOptionId
          ? { membershipBillingOptionId: membershipOffer.billingOptionId }
          : {}),
        ...(approvalMethod === 'SIGNATURE' && {
          customerSignatureName: sigName.trim(),
          customerSignatureText: sigNote.trim() || undefined,
          customerSignatureImage: signatureImage,
        }),
        consentGranted,
        consentUserAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        lineItems: approvalLineItems,
      });

      // REQUIRED for money correctness — do not remove. customerApprove
      // reprices on an optional-selection change, so the deposit CTA (which
      // only renders after approval) would otherwise show pre-approval amounts
      // while Stripe charges the repriced ones.
      //
      // The seed, not the refetch, is what guarantees this. A failed refetch
      // does NOT reject: TanStack swallows query errors unless `throwOnError`
      // is set, so the GET failing leaves the PREVIOUS payload in cache and
      // resolves — indistinguishable from success here. Writing the approval
      // response (which is the repriced estimate, already in hand) first means
      // the refetch can only ever improve on a correct value, never leave a
      // stale one behind. Both helpers are unit-tested.
      seedApprovedPublicEstimate<Estimate, DepositSurcharge>(
        queryClient,
        id,
        approved,
        estimateQueryKey,
      );
      const approvedEstimate = approved.estimate
        ? ({
            ...estimate,
            ...approved.estimate,
            depositCollectedSummary:
              approved.depositCollectedSummary === undefined
                ? estimate.depositCollectedSummary
                : approved.depositCollectedSummary,
          } as Estimate)
        : null;
      const refreshed = await refetchPublicEstimate<PublicEstimateResponse>(
        queryClient,
        id,
        estimateQueryKey,
      );

      // Re-render from the SERVER's approved estimate whenever approval can
      // change money. Prefer the refetch and fall back to the approval response
      // so a swallowed GET failure can never put the pre-approval values back.
      const repricedEstimate = refreshed?.estimate
        ? {
            ...refreshed.estimate,
            depositCollectedSummary:
              refreshed.depositCollectedSummary === undefined
                ? refreshed.estimate.depositCollectedSummary
                : refreshed.depositCollectedSummary,
          }
        : approvedEstimate;
      requireApprovedEstimateForSignedMembership(repricedEstimate, signedMembershipApproval);
      if ((optionalSelectionsChanged || acceptingMembershipOffer) && repricedEstimate) {
        try {
          // Fetch this sibling explicitly. The broad prefix refetch above is
          // best-effort and TanStack can leave the old successful value cached
          // when its request fails; reading that cache would combine the new
          // approved totals with stale installment stages in the signed PDF.
          const refreshedPlan = await fetchPublicPaymentPlanPreview<PublicPaymentPlanPreview>({
            operationsUrl: OPS_URL,
            id,
            tenantId: repricedEstimate.tenantId,
          });
          queryClient.setQueryData(publicPaymentPlanKey(id), refreshedPlan);
          await renderAndRegisterSignedPdf(
            repricedEstimate,
            'repriced_after_approval',
            refreshedPlan,
          );
        } catch (repricePdfErr) {
          if (signedMembershipApproval) {
            throw repricePdfErr instanceof Error
              ? repricePdfErr
              : new Error(
                  'The estimate was approved, but its signed PDF could not be prepared. Please contact support before retrying.',
                );
          }
          console.warn('Could not register the PDF from the repriced estimate:', repricePdfErr);
        }
      }

      setActionDone('approved');
      // Close the single-scope approve modal once the approval commits; the
      // document flips to APPROVED and the post-approval deposit CTA takes over.
      setApproveModalOpen(false);
      toast.success(`${documentLabel} approved!`);
      // Approve and pay: judged on the SERVER's approved deposit (approval
      // reprices it), and charged at that stored figure.
      if (
        !payFirst &&
        repricedEstimate &&
        isDepositOwed({
          depositAmount: repricedEstimate.depositAmount,
          depositCollected: Boolean(repricedEstimate.depositCollected),
          depositClearing: Boolean(repricedEstimate.depositClearing),
        })
      ) {
        openDepositPayment(
          refreshed?.depositSurcharge ?? approved.depositSurcharge ?? depositSurcharge,
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setSubmitting(true);
    try {
      await (transport?.decline ?? customerDecline)(id, estimate.tenantId, declineReason);
      setActionDone('declined');
      setDeclineModalOpen(false);
      toast.success('Estimate declined');
    } catch {
      toast.error('Failed to decline. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const effectiveStatus = actionDone
    ? actionDone === 'approved'
      ? 'APPROVED'
      : 'DECLINED'
    : estimate.status;
  // Display acceptance after conversion without changing the actual status:
  // approval must continue to exclude CONVERTED. The deposit does not: it is
  // still owed once the estimate became a job (shouldShowDepositCta).
  const isApprovedPresentation = effectiveStatus === 'APPROVED' || effectiveStatus === 'CONVERTED';
  // Financing belongs where money can still change hands: a live quote the
  // customer can still approve, or one they have just approved and now owe a
  // deposit on. Past the valid-until date approval is blocked, and at
  // EXPIRED/DECLINED it is gone for good — there the CTA leads nowhere, and
  // reads especially oddly beside its own "before approving this estimate"
  // copy. Keyed off `effectiveStatus`, not `canAct`, so an in-session approval
  // (which clears `canAct`) keeps its financing offer even when the approved
  // view is unavailable for want of a rendered PDF.
  const showDecisionFinancing =
    isApprovedPresentation || (ACTIONABLE_STATUSES.includes(effectiveStatus) && !isExpiredByDate);
  const latestDepositPaymentStatus = estimate.latestDepositPaymentStatus?.toUpperCase();
  const depositCollected = Boolean(estimate.depositCollected);
  const depositDue = Number(estimate.depositAmount) > 0 && !depositCollected;
  // A bank payment the processor accepted and has not settled. Nothing more to
  // pay, and approval unlocks: the server counts it as the deposit.
  const depositClearing = Boolean(estimate.depositClearing) && !depositCollected;
  // A bank payment for the deposit that failed or was returned after the
  // processor accepted it — possibly days after approval. The deposit is due
  // again; the slot says why above the pay CTA, even on a cold load.
  const depositFailed = Boolean(estimate.depositFailed) && depositDue && !depositClearing;
  // Either of those is a paper check staff scanned at the counter: the slot
  // says "check" rather than "bank transfer".
  const depositByCheck = Boolean(estimate.depositByCheck) && (depositClearing || depositFailed);
  // Two orders. Approve and pay: the customer signs first and the deposit is
  // paid right after, whether or not it is marked "Required to approve" (the
  // server holds the job back until a required one is in) — the CTA shows
  // once APPROVED. `payFirst` (the staff in-person checkout) collects a
  // required deposit FIRST — the CTA shows while the quote is still
  // actionable and approval stays locked until it settles (the backend
  // refuses the office's approval, so this is guidance, not the control).
  // Both extracted to depositCta.ts for unit-testability.
  // The deposit owed RIGHT NOW when it is paid first: priced on the add-ons
  // ticked, so a quote sent with a $0 stored deposit (all-optional work on a
  // percentage) still owes one once an extra is selected.
  const requiredDepositDue = payFirst
    ? optionalSelectionsChanged
      ? selectionDeposit
      : Number(estimate.depositAmount) || 0
    : estimate.depositAmount;
  const canPayDeposit = shouldShowDepositCta({
    depositAmount: requiredDepositDue,
    depositCollected,
    effectiveStatus,
    depositRequired: payFirst,
    actionable: canAct && !isExpiredByDate,
    depositClearing,
  });
  const approvalBlockedByDeposit =
    canAct &&
    isApprovalBlockedByDeposit({
      depositAmount: requiredDepositDue,
      depositCollected,
      depositRequired: payFirst,
      depositClearing,
    });
  // Approving leads straight into paying: the primary action says so. Sized
  // on the scope being accepted, since approval reprices the deposit on it.
  const approveAndPay =
    canAct &&
    !payFirst &&
    isDepositOwed({ depositAmount: selectedDeposit, depositCollected, depositClearing });
  // The pre-approval deposit is priced on the add-ons ticked right now.
  depositSelectionRef.current = approvalBlockedByDeposit
    ? { lineItems: approvalLineItems }
    : undefined;
  // Once a required deposit is in before approval (paid first, or on a page
  // opened before Approve and pay), the add-ons it was priced on are the
  // scope: the toggles lock (the backend refuses an approval that changes
  // them). The membership offer stays open — it only lowers the work total.
  const depositSelectionLocked =
    canAct && Boolean(estimate.depositRequired) && (depositCollected || depositClearing);
  // The figure the deposit CTA charges: while a required deposit is paid
  // before approval, the list-priced share of the add-ons ticked (the server
  // reprices to exactly this before charging); otherwise the stored amount,
  // which is authoritative once the document is approved.
  const depositChargeAmount = approvalBlockedByDeposit
    ? Number(requiredDepositDue) || 0
    : Number(estimate.depositAmount) || 0;
  // The server previewed the fee for the STORED deposit; while the base
  // follows the customer's add-on selection, the disclosed fee and card total
  // must follow it too, or the page promises a total the charge won't match.
  const depositSurchargeForCharge = approvalBlockedByDeposit
    ? rescaleDepositSurcharge(depositSurcharge, depositChargeAmount)
    : depositSurcharge;
  // Sent here right after approving a proposal option that carries a deposit:
  // open the payment once, as soon as the caller asks (after its own
  // post-approval work, so a Checkout redirect cannot cut that short).
  const depositOnLoadHandledRef = useRef(false);
  useEffect(() => {
    if (!openDepositOnLoad || depositOnLoadHandledRef.current) return;
    depositOnLoadHandledRef.current = true;
    if (canPayDeposit) openDepositPayment(depositSurcharge);
    // Acted on once, when the caller turns it on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDepositOnLoad]);
  // The fee note is history: the server gates `show` on a COLLECTED card
  // deposit and never on the current deposit amount, which can be repriced to
  // 0 afterwards — so the note's reachability must not hang off depositAmount
  // either. It stays hidden while the deposit is still payable, since claiming
  // a fee was already charged next to a live Pay-Deposit CTA would contradict
  // that CTA's own pre-payment disclosure.
  const showServiceFeeNote = Boolean(serviceFee?.show) && !canPayDeposit;
  // The approved-view aside carries the deposit panel, the financing CTA AND
  // the service-fee note, so it renders when ANY of them has something to
  // show. Gating the whole aside on a deposit hid financing outright on
  // approved estimates that don't take one — and those are exactly the
  // estimates where financing matters most, since there is no deposit
  // conversation to raise it in.
  // Converted estimates cannot collect another deposit here. Keep a paid
  // receipt, but do not show a contradictory unpaid card without a payment action.
  const showApprovedDeposit =
    Number(estimate.depositAmount) > 0 && (canPayDeposit || depositCollected || depositClearing);
  const showApprovedAside = showApprovedDeposit || !!wisetack || showServiceFeeNote;
  const isDepositDisputedAfterRedirect =
    depositSearchState === 'success' &&
    (latestDepositPaymentStatus === 'DISPUTED' ||
      latestDepositPaymentStatus === 'DISPUTE_UNDER_REVIEW' ||
      latestDepositPaymentStatus === 'CHARGEBACK_LOST') &&
    depositDue;
  const isDepositFailedAfterRedirect =
    depositSearchState === 'success' && latestDepositPaymentStatus === 'FAILED' && depositDue;
  const isSettlingDeposit =
    depositSearchState === 'success' &&
    depositDue &&
    !depositClearing &&
    !isDepositDisputedAfterRedirect &&
    !isDepositFailedAfterRedirect;

  // Download the estimate PDF. Hoisted so both the inline action button and the
  // single-scope letterhead's download icon trigger the exact same flow (prefer
  // a freshly-signed blob, else the stored PDF, else render client-side).
  const handleDownloadPdf = async () => {
    try {
      const paymentPlanForDownload = acceptingMembershipOffer
        ? await fetchPublicPaymentPlanPreview<PublicPaymentPlanPreview>({
            operationsUrl: OPS_URL,
            id,
            tenantId: estimate.tenantId,
            acceptMembershipOffer: true,
            membershipBillingOptionId: membershipOffer?.billingOptionId,
          })
        : paymentPlan;

      // A stored/signed blob represents the persisted non-member document.
      // While the recipient is previewing membership terms, render the frozen
      // member-priced projection instead of downloading that stale artifact.
      if (signedPdfBlob && !acceptingMembershipOffer) {
        const blobUrl = URL.createObjectURL(signedPdfBlob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${pdfBaseName}.pdf`;
        a.click();
        URL.revokeObjectURL(blobUrl);
        return;
      }

      if (shouldUseStoredEstimatePdf && !optionalSelectionsChanged && !acceptingMembershipOffer) {
        const res = await fetch(`${OPS_URL}/estimates/public/${id}/pdf/download-url`);
        if (res.ok) {
          const { url } = await res.json();
          const pdfRes = await fetch(url);
          if (pdfRes.ok) {
            const blob = await pdfRes.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = `${pdfBaseName}.pdf`;
            a.click();
            URL.revokeObjectURL(blobUrl);
            return;
          }
        }
      }

      const [{ pdf }, { EstimateDocumentPdf }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('@/components/documents/estimates/EstimateDocumentPdf'),
      ]);
      const pdfEstimate = await prepareEstimatePdfImages(membershipAdjustedEstimate, {
        audience: transport?.queryKey[0] === 'portal' ? 'portal' : 'public',
      });
      const pdfDesignEstimate = mergeEstimatePdfImages(selectedEstimate, pdfEstimate);
      const blob = await pdf(
        <EstimateDocumentPdf
          estimate={pdfEstimate}
          company={company}
          attachmentImages={attachmentImages}
          customFields={customFields}
          wisetack={wisetack}
          paymentPlan={paymentPlanForDownload}
          serviceFee={serviceFee}
          timeZone={timezone}
          resolvedDocLayout={resolvedDocLayout}
          totalsOverride={membershipTotalsOverride}
          designEstimate={pdfDesignEstimate}
          presentedByName={presentedByName}
        />,
      ).toBlob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${pdfBaseName}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);

      if (!optionalSelectionsChanged && !acceptingMembershipOffer) {
        try {
          const uploadRes = await fetch(`${OPS_URL}/estimates/public/${id}/pdf-upload-url`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });
          if (uploadRes.ok) {
            const { uploadUrl, fileUrl } = await uploadRes.json();
            await fetch(uploadUrl, {
              method: 'PUT',
              body: blob,
              headers: { 'Content-Type': 'application/pdf' },
            });
            await fetch(`${OPS_URL}/estimates/public/${id}/pdf`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ url: fileUrl, reason: 'generated' }),
            });
          }
        } catch {
          // Non-fatal
        }
      }
    } catch {
      toast.error('Failed to download PDF. Please try again.');
    }
  };

  // Single-scope aligns the primary approve button to the terracotta brand
  // accent (matching the sticky bar + the artifact); itemized keeps the neutral
  // black it has always used.
  const approveButtonClass =
    presentation === 'SINGLE_SCOPE'
      ? 'w-full bg-[#E4682F] text-white hover:bg-[#C0561F]'
      : 'w-full bg-black text-white hover:bg-black/90';

  // Whether the customer can approve ON this page (SIGNATURE/ONLINE flows). VERBAL
  // and EMAIL route approval off-page, so neither the sign flow nor the pill's
  // approve action apply to them.
  const canApproveOnPage = canAct && approvalMethod !== 'VERBAL' && approvalMethod !== 'EMAIL';

  return {
    documentLabel,
    documentNumber,
    // Props (forwarded verbatim so the view + slots read a single source).
    estimate: presentedEstimate,
    payerContactId:
      payerContactId === undefined ? estimate.contactId : (payerContactId ?? undefined),
    embedded,
    company,
    documentCompany,
    activePdf,
    customFields,
    v,
    presentation,
    presentedByName,
    reviews,
    id,
    depositSearchState,
    depositSurcharge: depositSurchargeForCharge,
    payment,
    serviceFee,
    wisetack,
    timezone,
    resolvedDocLayout,
    // Derived document values.
    pdfBaseName,
    serviceAddress,
    customerVisibleTitle,
    estimateDiscountLabel,
    customerNotes,
    attachmentImages,
    paymentPlan,
    // Action-layer state + setters.
    sigName,
    setSigName,
    sigNote,
    setSigNote,
    sigHasInk,
    setSigHasInk,
    showModalTerms,
    setShowModalTerms,
    showDeclineForm,
    setShowDeclineForm,
    approveModalOpen,
    setApproveModalOpen,
    declineModalOpen,
    setDeclineModalOpen,
    declineChoice,
    setDeclineChoice,
    declineReason,
    setDeclineReason,
    termsAccepted,
    setTermsAccepted,
    consentGranted,
    setConsentGranted,
    consentRequired,
    setConsentRequired,
    submitting,
    redirectingDeposit,
    sigPadRef,
    actionDone,
    signedPdfBlob,
    pendingDepositMethod,
    depositMutation,
    startDeposit,
    payDepositWithPayabli,
    depositAchSettling,
    depositAchFailed,
    // Which rail the settling attempt is on; ACH wording only for 'BANK'.
    depositAttemptRail: depositAttempt?.rail ?? null,
    // Signed-PDF / stored-PDF display.
    shouldUseStoredEstimatePdf,
    pdfDisplayUrl,
    // Selection engine outputs.
    optionalSelections,
    toggleOptional,
    requiredRows,
    displayOptionalOffers,
    optionalSelectionsChanged,
    selectedLines,
    totals,
    subtotalWithoutSetupFee: totals.subtotal - requiredSetupFee,
    requiredSubtotal: requiredSubtotal - requiredSetupFee,
    selectedOptionalRows,
    approvalLineItems,
    selectedGrandTotal,
    approvalGrandTotal,
    approvalTaxTotal,
    selectedMembershipDiscountTotal,
    displayMembershipSavings,
    membershipSavingsPlanNames,
    membershipPlanPrice,
    membershipSetupFee,
    selectedDiscountTotal,
    selectedDeposit,
    selectedEstimate,
    // The live "effective" estimate the customer document renders from: selected
    // optionals + accepted membership folded in, so the block document matches the
    // interactive totals exactly (same object the generated/signed PDF uses).
    membershipAdjustedEstimate,
    membershipOffer,
    acceptMembershipOffer: acceptingMembershipOffer,
    setAcceptMembershipOffer,
    selectedMembershipBillingOptionId,
    setSelectedMembershipBillingOptionId,
    // Status + gating.
    canAct,
    validUntilDate,
    isExpiredByDate,
    expiredWhileActionable,
    taxBreakdown,
    approvalMethod,
    offlineNote,
    terms,
    hasTerms,
    handleApprove,
    handleDecline,
    effectiveStatus,
    isApprovedPresentation,
    showDecisionFinancing,
    depositCollected,
    depositDue,
    depositClearing,
    depositFailed,
    depositByCheck,
    canPayDeposit,
    approvalBlockedByDeposit,
    approveAndPay,
    depositAutoOpen,
    depositSelectionLocked,
    depositChargeAmount,
    showServiceFeeNote,
    showApprovedAside,
    showApprovedDeposit,
    isDepositDisputedAfterRedirect,
    isDepositFailedAfterRedirect,
    isSettlingDeposit,
    handleDownloadPdf,
    approveButtonClass,
    canApproveOnPage,
  };
}

/** Everything the public estimate page's action layer exposes to its view + slots. */
export type QuoteActionValue = ReturnType<typeof useQuoteActionState>;

const QuoteActionContext = createContext<QuoteActionValue | null>(null);

/**
 * Owns the public estimate page's money/action layer and provides it to the
 * view body + slot components. Wraps BOTH render branches (the approved-PDF
 * early return and the main return) so the deposit CTA reads the same source in
 * either.
 */
export function QuoteActionProvider({
  children,
  ...props
}: EstimateViewProps & { children: ReactNode }) {
  const value = useQuoteActionState(props);
  return <QuoteActionContext.Provider value={value}>{children}</QuoteActionContext.Provider>;
}

export function useQuoteActions(): QuoteActionValue {
  const ctx = useContext(QuoteActionContext);
  if (!ctx) {
    throw new Error('useQuoteActions must be used within a QuoteActionProvider');
  }
  return ctx;
}
